import { useContext, useCallback } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { t } from 'ttag'
import format from 'date-fns/format'

import { PG, SLB, NAVIANCEBLUE } from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { Heading as HeadingBase } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Button } from '@h2/components/buttons/button'
import { EggTimerIcon } from '@h2/components/icons/eggTimer'
import { WarningIcon } from '@h2/components/icons/warning'
import { Card } from '@h2/common/components/card'
import { Globals } from '@h2/globals'

import { Create } from '../highlights/create'
import { Controls } from '../controls'

const Heading = styled(HeadingBase)`
  display: flex;
  flex-direction: column;
  margin: 0;

  > span[role='img'] {
    margin-bottom: 15px;

    svg {
      width: auto;
      height: 130px;
    }
  }
`
const Wrap = styled(Flex)`
  height: 100%;
  flex-direction: column;
`
const DefinitionList = styled.dl`
  font-size: 16px;
  color: black;
  width: 100%;
  float: left;
  margin-bottom: 15px;

  dt::after {
    content: ':';
    margin-right: 5px;
  }

  dt {
    float: left;
  }

  dd:first-of-type {
    margin-bottom: 5px;
  }
`
const AmendButton = styled(Button)`
  font-size: 18px;
`
const Pending = ({ inDialog, properties, renderPanel }) => {
  const { isNavianceUser, backToNavianceURL } = useContext(Globals)
  const onClickFinished = useCallback(
    evt => {
      evt.preventDefault()
      //This function is invoked only for naviance user. Production redirection url is assigned if there is some error in getting the URL.
      window.location.assign(backToNavianceURL || 'https://student.naviance.com/')
    },
    [backToNavianceURL]
  )

  return (
    <Card inDialog={inDialog}>
      <Card.Highlight
        color={isNavianceUser ? NAVIANCEBLUE : properties.isNavigator ? SLB : PG}>
        <Create
          type={
            isNavianceUser
              ? 'navianceUser'
              : properties.isNavigator
              ? 'navigator'
              : 'explorer'
          }
        />
      </Card.Highlight>
      <Card.Content>
        <Wrap>
          <Heading level="2" align="center">
            {properties.removed ? (
              <>
                <WarningIcon size="large" />
                <span>{t`Your request has been declined.`}</span>
              </>
            ) : (
              <>
                <EggTimerIcon size="large" />
                <span>{t`Almost done, awaiting approval`}</span>
              </>
            )}
          </Heading>
          <Text align="center" size="large" my="20px">
            {properties.removed
              ? t`The authorized person you indicated has chosen not to approve your access to this site.  If you think this was done in error, you can amend your request and try again.`
              : isNavianceUser
              ? t`Headed2 has notified your authorized person via email. After they confirm approval, your account will be ready to use. Check the approval status in Naviance Student by going to My Account in the About Me section.`
              : t`We emailed your parent or guardian. After they approve your account, you can start using Headed2. Return to this page to check on the approval status.`}
          </Text>
          {!properties.removed && (
            <DefinitionList>
              <dt>{t`Parent or guardian email`}</dt>
              <dd>{properties.authorizedPersonEmail}</dd>
              <dt>{t`Date sent`}</dt>
              <dd>{format(new Date(), 'dd LLL, yyyy')}</dd>
            </DefinitionList>
          )}
          <Controls>
            <Box width="250px" margin="0 auto">
              <AmendButton
                isFullWidth
                isFullHeight
                variant="outlined-cta"
                size="large"
                align="center"
                onClick={() => renderPanel('parentApproval', { removed: null })}>
                {t`Change approval request`}
              </AmendButton>
            </Box>
            {isNavianceUser && (
              <Box width="250px" margin="0 auto">
                <AmendButton
                  isFullWidth
                  isFullHeight
                  variant="cta"
                  size="large"
                  align="center"
                  onClick={onClickFinished}>
                  {t`Return to Naviance`}
                </AmendButton>
              </Box>
            )}
          </Controls>
        </Wrap>
      </Card.Content>
      <Card.Detour>
        <Text>
          <AmendButton
            isFullWidth
            isFullHeight
            variant="outlined-cta"
            size="large"
            align="center"
            onClick={() => renderPanel('parentApproval', { removed: null })}>
            {t`Change approval request`}
          </AmendButton>
          {isNavianceUser && (
            <AmendButton
              isFullWidth
              isFullHeight
              variant="outlined-cta"
              size="large"
              align="center"
              onClick={onClickFinished}>
              {t`Return to Naviance`}
            </AmendButton>
          )}
        </Text>
      </Card.Detour>
    </Card>
  )
}

Pending.propTypes = {
  inDialog: PropTypes.bool,
  properties: PropTypes.object.isRequired,
  renderPanel: PropTypes.func.isRequired,
  removed: PropTypes.string
}

export { Pending }
